import { AddCircle, Edit } from '@mui/icons-material';
import { Button, ClassNameMap, IconButton, Tooltip } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridToolbarContainer,
} from '@mui/x-data-grid-premium';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import {
  Decision,
  useDecisionRegistryData,
} from '~/hooks/useDecisionRegistryData';
import { LegacyCustomField } from '~/legacy-components/LegacyCustomFields';
import {
  AnswerPreProcess,
  CustomField,
} from '~/legacy-components/LegacyCustomFields/types';
import { CustomFieldStyleKeys } from '~/legacy-components/LegacyCustomFields/useStyles';
import { LegacyLoading } from '~/legacy-components/LegacyLoading';
import { basicFileDownload } from '~/services/Http';
import { isEmployee } from '~/services/Permissions';

import { DecisionRegistryForm } from './components/DecisionRegistryForm';

export const DecisionRegistryTab = () => {
  const { t } = useTranslation();
  const roles = useLoggedInUserRoles();

  const isEmployeeRole = isEmployee(roles);
  const { selectedEmployeeId: empId } = useSelectedEmployeeId();
  const [dialogOpen, setOpenDialog] = useState(false);
  const [forEditDecision, setForEditDecision] = useState<Decision>();

  const { setValue, getValues, handleSubmit } = useForm<AnswerPreProcess>();

  const { data, customSelectFields, fetchDecisionRegistryData } =
    useDecisionRegistryData();

  const updateCustomFields = useCallback(
    (customData) => {
      const fieldVal = Object.keys(customData)[0];
      setValue(fieldVal[0], customData[fieldVal]);
    },
    [setValue],
  );

  const handleClose = () => {
    setOpenDialog(false);
    setForEditDecision(undefined);
  };

  const handleEdit = (decision: Decision) => {
    setOpenDialog(true);
    setForEditDecision(decision);
    if (data.editDrAllCustomFields[decision.id]) {
      customSelectFields.forEach((code: string) => {
        const field = data.editDrAllCustomFields[decision.id].find(
          (fieldToFind) => fieldToFind.code === code,
        );
        setValue(code, field?.value ?? '');
      });
    }
    setOpenDialog(true);
  };

  const handleFileDownload = (attachmentId: number, attachmentName: string) => {
    basicFileDownload(
      `/bin/employee/${empId}/file/${attachmentId}`,
      attachmentName,
    );
  };

  if (data.isFetching) {
    return <LegacyLoading />;
  }

  const renderAddButton = () =>
    !isEmployeeRole ? (
      <GridToolbarContainer>
        <IconButton onClick={() => setOpenDialog(true)}>
          <Tooltip title={t('laAdd')}>
            <AddCircle color="primary" />
          </Tooltip>
        </IconButton>
      </GridToolbarContainer>
    ) : (
      <></>
    );

  function EditToolbar() {
    return renderAddButton();
  }

  const EditButtonAction = (decision: Decision) =>
    !isEmployeeRole ? (
      <IconButton onClick={() => handleEdit(decision)}>
        <Tooltip
          title={
            decision.isSystemGenerated ? t('laSystemEntry') : t('laManualEntry')
          }
        >
          <Edit color={decision.isSystemGenerated ? 'primary' : 'secondary'} />
        </Tooltip>
      </IconButton>
    ) : (
      <></>
    );

  const rows = data.decisionRegistryData;

  type Row = typeof rows[number];

  const cols = [
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      cellClassName: 'actions',
      width: 100,
      getActions: ({ row }: { row: Row }) => [EditButtonAction(row)],
    },
    { field: 'type', headerName: t('laType'), width: 150 },
    { field: 'title', headerName: t('laTitle'), width: 200 },
    {
      field: 'inventoryNumber',
      headerName: t('laEquipmentNumber'),
      width: 150,
    },
    { field: 'date', headerName: t('laDecisionDate'), width: 150 },
    { field: 'maker', headerName: t('laDecisionMaker'), width: 200 },
    { field: 'info', headerName: t('laDecisionInfo'), width: 200 },
    ...data.fieldLang.map((field) => ({
      field: field.name,
      type: 'actions',
      headerName: field.name,
      width: 200,
      getActions: ({ row }: { row: Row }) => {
        return data.editDrAllCustomFields[row.id]
          ? data.editDrAllCustomFields[row.id].map((field: CustomField) => {
              return (
                <LegacyCustomField
                  fieldDetails={field}
                  isSaving={false}
                  idEditMode={false}
                  classes={{} as ClassNameMap<CustomFieldStyleKeys>}
                />
              );
            })
          : data.addDrEmptyCustomFields.length
          ? data.addDrEmptyCustomFields.map((field: CustomField) => {
              return (
                <LegacyCustomField
                  fieldDetails={field}
                  isSaving={false}
                  idEditMode={false}
                  classes={{} as ClassNameMap<CustomFieldStyleKeys>}
                />
              );
            })
          : [];
      },
    })),
    {
      field: 'attachmentName',
      type: 'actions',
      headerName: t('laAttachment'),
      width: 200,
      getActions: ({ row }: { row: Row }) => [
        row.attachmentName ? (
          <Button
            onClick={() =>
              handleFileDownload(
                row.attachmentId as number,
                row.attachmentName as string,
              )
            }
          >
            {row.attachmentName}
          </Button>
        ) : (
          <Button></Button>
        ),
      ],
    },
    { field: 'reminderDate', headerName: t('laReminder'), width: 200 },
  ];

  return (
    <>
      <DataGridPremium
        rows={rows}
        columns={cols as GridColDef[]}
        slots={{
          toolbar: EditToolbar,
        }}
        pagination
        paginationModel={{ pageSize: 5, page: 0 }}
        pageSizeOptions={[5, 10, 25, 50, 100]}
      />
      <DecisionRegistryForm
        decision={forEditDecision as Decision}
        employeeId={empId}
        dialogOpen={dialogOpen}
        handleClose={handleClose}
        editDrAllCustomFields={data.editDrAllCustomFields}
        addDrEmptyCustomFields={data.addDrEmptyCustomFields}
        updateCustomFields={updateCustomFields}
        getValues={getValues}
        setValue={setValue}
        errors={{}}
        handleSubmit={handleSubmit}
        fetchDecisionRegistryData={fetchDecisionRegistryData}
      />
    </>
  );
};
